import { useEffect, useMemo, useState } from 'react';
import { Alert, Badge, Button, ConfirmDialog, Input, Select, Switch, useToast } from '@dreamtree-org/twreact-ui';
import { ArrowDown, ArrowUp, Plus, Trash2 } from 'lucide-react';
import { SectionCard, QueryState } from '@/components';
import { useQuery, useMutation } from '@/lib/useQuery';
import { useStoreCustomFields } from '@/lib/productTypes';
import { errorText } from '@/lib/format';
import { useCan } from '@rbac/useCan';
import { draftOf, fieldsOf, draftErrors, moveField, serverErrors, kindOf as kindIn } from '../../lib/customFields';

/**
 * Custom fields (ADR 0009 §4, MERCH-86): a store adds its own spec fields to each product type
 * it sells, beside the platform's: text, number (with a unit), yes/no or a pick list, at most
 * `max` per type. They show on the product page and, when filterable, in the shop's filters;
 * never on the marketplace. Saving replaces the type's list (Category.setCustomAttributes,
 * inventory:update, audited). A deleted field's values stay stored but hidden, and its name's
 * key cannot be used again.
 */
const NONE = Object.freeze([]);

export default function CustomFieldsTab() {
  const { toast } = useToast();
  const can = useCan();
  const canEdit = can('inventory', 'update');
  const store = useQuery('storeProductTypes', {}, { select: (r) => (r && r.data) || [] });
  const custom = useStoreCustomFields();
  const save = useMutation('setStoreCustomAttributes');
  const enabled = useMemo(() => (store.data || []).filter((t) => t.enabled), [store.data]);
  const [typeKey, setTypeKey] = useState('');
  const [draft, setDraft] = useState([]);
  const [errors, setErrors] = useState({});
  const [formError, setFormError] = useState('');
  const [removing, setRemoving] = useState(null);

  const type = enabled.find((t) => t.key === typeKey) || null;
  const saved = useMemo(() => (type && custom.types[type.key]) || NONE, [type, custom.types]);
  const kinds = custom.kinds.map((k) => ({ value: k.key, label: k.label }));
  const kindOf = (key) => kindIn(custom.kinds, key);

  useEffect(() => {
    if (!typeKey && enabled.length) setTypeKey(enabled[0].key);
  }, [enabled, typeKey]);
  useEffect(() => {
    setDraft(draftOf(saved));
    setErrors({});
    setFormError('');
  }, [saved]);

  const dirty = JSON.stringify(fieldsOf(draft, custom.kinds)) !== JSON.stringify(fieldsOf(draftOf(saved), custom.kinds));
  const full = draft.length >= custom.max;
  const patch = (i, p) => setDraft((d) => d.map((f, j) => (j === i ? { ...f, ...p } : f)));
  const add = () => setDraft((d) => [...d, { id: `new-${Date.now()}`, label: '', kind: 'text', unit: '', choices: '', filter: false, saved: false }]);
  const remove = (i) => {
    setDraft((d) => d.filter((_, j) => j !== i));
    setRemoving(null);
  };

  const submit = async () => {
    const e = draftErrors(draft, { max: custom.max, kinds: custom.kinds });
    setErrors(e);
    setFormError('');
    if (Object.keys(e).length) return;
    try {
      await save.run({ product_type: type.key, fields: fieldsOf(draft, custom.kinds) });
      toast.success('Saved', `Custom fields for ${type.plural.toLowerCase()} are updated.`);
      custom.reload();
    } catch (ex) {
      setErrors(serverErrors(ex));
      setFormError(errorText(ex));
    }
  };

  return (
    <QueryState query={store}>
      {() => (
        <SectionCard
          title="Custom fields"
          actions={canEdit && type ? <Button onClick={submit} loading={save.loading} disabled={!dirty}>Save</Button> : null}
        >
          <p className="text-sm opacity-70">
            Add your own details to each type you sell, after the standard specs: text, a number with a unit, yes/no or a pick list (at most {custom.max} per type).
            They show on the product page; filterable ones also appear in your shop&apos;s filters. They never appear on the marketplace.
          </p>
          {!enabled.length ? <Alert variant="info">Choose what you sell first (the What you sell tab).</Alert> : null}
          {enabled.length ? (
            <div className="max-w-xs">
              <Select label="Product type" options={enabled.map((t) => ({ value: t.key, label: t.plural }))} value={typeKey} onChange={(v) => v && setTypeKey(String(v))} closeOnSelect />
            </div>
          ) : null}
          {formError ? <Alert variant="error">{formError}</Alert> : null}
          {errors.fields ? <Alert variant="error">{errors.fields}</Alert> : null}
          {type ? (
            <QueryState query={custom}>
              {() => (
                <div className="space-y-3">
                  {draft.length ? (
                    <ol className="space-y-3" aria-label={`Custom fields for ${type.plural}`}>
                      {draft.map((f, i) => (
                        <li key={f.id} className="space-y-2 rounded-lg border border-[var(--app-border)] p-3">
                          <div className="grid gap-3 sm:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)]">
                            <Input label="Field name" value={f.label} maxLength={60} disabled={!canEdit} onChange={(e) => patch(i, { label: e.target.value })} error={errors[`fields.${i}.label`] || errors[`fields.${i}.key`]} />
                            <Select
                              label="Kind"
                              options={kinds}
                              value={f.kind}
                              disabled={!canEdit || f.saved}
                              helperText={f.saved ? 'Fixed once saved' : undefined}
                              onChange={(v) => v && patch(i, { kind: String(v) })}
                              closeOnSelect
                              error={errors[`fields.${i}.kind`]}
                            />
                            {kindOf(f.kind).unit ? (
                              <Input label="Unit (optional)" value={f.unit} maxLength={12} placeholder="kg, cm, mAh" disabled={!canEdit} onChange={(e) => patch(i, { unit: e.target.value })} error={errors[`fields.${i}.unit`]} />
                            ) : <div />}
                          </div>
                          {kindOf(f.kind).options ? (
                            <Input
                              label="Choices"
                              helperText="Separate them with commas, in the order shoppers see them."
                              value={f.choices}
                              disabled={!canEdit}
                              onChange={(e) => patch(i, { choices: e.target.value })}
                              error={errors[`fields.${i}.options`]}
                            />
                          ) : null}
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <Switch checked={f.filter} disabled={!canEdit} onChange={(on) => patch(i, { filter: on })} rightLabel="Shoppers can filter by it" ariaLabel={`Shoppers can filter by ${f.label || 'this field'}`} />
                            <div className="flex items-center gap-1">
                              {f.saved ? <Badge size="sm" variant="secondary">{f.key}</Badge> : <Badge size="sm" variant="info">New</Badge>}
                              {canEdit ? (
                                <>
                                  <Button size="sm" variant="ghost" aria-label={`Move ${f.label || 'field'} up`} disabled={i === 0} onClick={() => setDraft((d) => moveField(d, i, -1))}><ArrowUp size={16} /></Button>
                                  <Button size="sm" variant="ghost" aria-label={`Move ${f.label || 'field'} down`} disabled={i === draft.length - 1} onClick={() => setDraft((d) => moveField(d, i, 1))}><ArrowDown size={16} /></Button>
                                  <Button size="sm" variant="ghost" aria-label={`Delete ${f.label || 'field'}`} onClick={() => (f.saved ? setRemoving(i) : remove(i))}><Trash2 size={16} /></Button>
                                </>
                              ) : null}
                            </div>
                          </div>
                        </li>
                      ))}
                    </ol>
                  ) : <p className="text-sm opacity-60">No custom fields for {type.plural.toLowerCase()} yet.</p>}
                  {canEdit ? (
                    <div className="flex flex-wrap items-center gap-3">
                      <Button variant="outline" leftIcon={<Plus size={16} />} onClick={add} disabled={full}>Add a field</Button>
                      <span className="text-xs opacity-60">{draft.length} of {custom.max}{full ? ' — the most a type can have' : ''}</span>
                    </div>
                  ) : null}
                </div>
              )}
            </QueryState>
          ) : null}
          <ConfirmDialog
            isOpen={removing != null}
            title={`Delete “${removing != null && draft[removing] ? draft[removing].label : ''}”?`}
            description="When you save, its values stay stored but are hidden from your shop and can no longer be filtered or set. Its name cannot be used for a new field."
            confirmLabel="Delete field"
            destructive
            onConfirm={() => remove(removing)}
            onCancel={() => setRemoving(null)}
          />
        </SectionCard>
      )}
    </QueryState>
  );
}
